import { allows } from "@giftforward/entitlements";
import CloseIcon from "@mui/icons-material/Close";
import { type ChangeEvent, type FormEvent, useEffect, useRef, useState } from "react";
import z from "zod";
import CountryDropdown from "@/components/giftExperience/CountryDropdown";
import { type Recipient, useCampaignBuilder } from "@/context/CampaignBuilderContext";
import { useMyEntitlements } from "@/hooks/useMyEntitlements";
import type { FormState } from "@/interfaces/CampaignBuilder";
import { AddRecipientField } from "./AddRecipientField";

const isValidEmail = (value: string) => z.email().safeParse(value.trim()).success;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (recipient: Recipient) => void;
  existingEmails: Set<string>;
}

const EMPTY: FormState = {
  firstName: "",
  lastName: "",
  email: "",
  toCompany: "",
  senderFirst: "",
  senderLast: "",
  fromEmail: "",
  senderSignature: "",
  senderCompany: "",
  recipientCountry: "",
  sendDate: "",
  sendTime: "",
};

const tomorrow = new Date();
tomorrow.setDate(tomorrow.getDate() + 1);
const TOMORROW = tomorrow.toISOString().split("T")[0];

export function AddRecipientModal({ isOpen, onClose, onAdd, existingEmails }: Props) {
  const { ent } = useMyEntitlements();
  const { scheduleMode, notificationMode } = useCampaignBuilder();
  // Recipient email is required only when GfG sends the gift (gfg). A manual/universal "link to a
  // list" recipient is handed a link and never emailed, so its email is optional.
  const requireRecipientEmail = notificationMode === "gfg";
  const canSetCountry = !!ent && allows(ent, "campaign_send", "regions", "international");
  const canSetSendDate = !!ent && allows(ent, "campaign_send", "scheduling", "per_recipient");
  // Per-recipient send date/time only make sense under "Send on different dates" — under Send ASAP
  // or Schedule for later every recipient shares one campaign-level date, so these fields are
  // meaningless and hidden. Still AND the tier gate: "different dates" is itself Pro-only, but a
  // draft saved as "different" whose plan later dropped per-recipient scheduling must not show them.
  const showSendDate = canSetSendDate && scheduleMode === "different";

  const [visible, setVisible] = useState(false);
  const [animated, setAnimated] = useState(false);
  const rafRef = useRef<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<1 | 2>(1);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Partial<FormState>>({});

  useEffect(() => {
    if (isOpen) {
      setForm(EMPTY);
      setErrors({});
      setStep(1);
      setVisible(true);
      rafRef.current = requestAnimationFrame(() => {
        setAnimated(true);
        firstInputRef.current?.focus();
      });
    } else {
      setAnimated(false);
      timerRef.current = setTimeout(() => setVisible(false), 270);
    }
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      if (timerRef.current != null) clearTimeout(timerRef.current);
    };
  }, [isOpen]);

  const saveFieldValue = (key: keyof FormState) => (e: ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validateStep1 = (): Partial<FormState> => {
    const next: Partial<FormState> = {};
    if (!form.firstName.trim()) next.firstName = "Required";
    // Required only for gfg; manual/universal recipients are never emailed, so a blank email is
    // allowed — but a value typed anyway must still be valid and not a duplicate.
    if (requireRecipientEmail && !form.email.trim()) {
      next.email = "Required";
    } else if (form.email.trim() && !isValidEmail(form.email)) {
      next.email = "Enter a valid email address";
    } else if (form.email.trim() && existingEmails.has(form.email.trim().toLowerCase())) {
      next.email = "A recipient with this email already exists";
    }
    return next;
  };

  const validateStep2 = (): Partial<FormState> => {
    const next: Partial<FormState> = {};
    if (!form.senderFirst.trim()) next.senderFirst = "Required";
    // Sender email is optional; validate the format only when one is entered.
    if (form.fromEmail.trim() && !isValidEmail(form.fromEmail)) {
      next.fromEmail = "Enter a valid email address";
    }
    if (!form.senderCompany.trim()) next.senderCompany = "Required";
    // Shown only under "Send on different dates". One of the two is enough: a date alone sends at
    // 9:00 AM in the campaign's zone, a time alone tomorrow — the same defaults as a bulk import
    // (GFG-443).
    if (showSendDate && !form.sendDate && !form.sendTime) {
      next.sendDate = "Enter a send date or time";
    }
    return next;
  };

  const handleNext = () => {
    const errs = validateStep1();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setStep(2);
  };

  const handleBack = () => {
    setErrors({});
    setStep(1);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const errs = validateStep2();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    onAdd({
      id: crypto.randomUUID(),
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      toCompany: form.toCompany.trim() || undefined,
      senderFirst: form.senderFirst.trim(),
      senderLast: form.senderLast.trim(),
      fromEmail: form.fromEmail.trim() || undefined,
      senderSignature: form.senderSignature.trim() || undefined,
      senderCompany: form.senderCompany.trim(),
      ...(canSetCountry && {
        recipientCountry: form.recipientCountry.trim() || undefined,
      }),
      ...(showSendDate && {
        sendDate: form.sendDate || undefined,
        sendTime: form.sendTime || undefined,
      }),
    });
    onClose();
  };

  if (!visible) return null;

  return (
    <div
      className={`fixed inset-0 z-10000 flex items-center justify-center p-6 bg-black/55 transition-opacity duration-270 ${animated ? "opacity-100" : "opacity-0"}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      aria-hidden={!isOpen}
    >
      <div
        className={`relative w-full max-w-166 bg-white rounded-card border border-smoke shadow-[0_24px_60px_rgba(0,0,0,0.25)] transition-[opacity,transform] duration-270 ${animated ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-recipient-title"
      >
        <form onSubmit={handleSubmit} noValidate>
          <div className="p-5 flex flex-col gap-5">
            {/* Top bar: step indicator + close */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div
                  className={`w-2 h-2 rounded-full transition-colors ${step === 1 ? "bg-rodeo-black" : "bg-smoke"}`}
                />
                <div
                  className={`w-2 h-2 rounded-full transition-colors ${step === 2 ? "bg-rodeo-black" : "bg-smoke"}`}
                />
                <span className="font-lato text-[12px] text-graphite ml-1">Step {step} of 2</span>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="flex items-center justify-center w-8 h-8 rounded-btn border border-smoke bg-white hover:bg-clear-dust transition-colors"
                aria-label="Close"
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </button>
            </div>

            {/* Header */}
            <div className="flex flex-col gap-1">
              <h1
                id="add-recipient-title"
                className="font-crimson text-[30px] font-normal text-rodeo-black leading-tight"
              >
                {step === 1 ? "Recipient info" : "Sender info"}
              </h1>
              <p className="font-lato text-[14px] text-graphite">
                {step === 1 ? "Who is receiving this gift?" : "Who is sending this gift?"} Required
                fields are marked with <span className="text-good-wine">*</span>
              </p>
            </div>

            {/* Step 1 — Recipient fields */}
            {step === 1 && (
              <div className="flex flex-col gap-4">
                <div className="flex gap-4">
                  <AddRecipientField
                    id="add-recipient-first-name"
                    label="First name"
                    required
                    value={form.firstName}
                    onChange={saveFieldValue("firstName")}
                    error={errors.firstName}
                    inputRef={firstInputRef}
                  />
                  <AddRecipientField
                    id="add-recipient-last-name"
                    label="Last name"
                    value={form.lastName}
                    onChange={saveFieldValue("lastName")}
                    error={errors.lastName}
                  />
                </div>

                <div className="flex gap-4">
                  <AddRecipientField
                    id="add-recipient-email"
                    label="Email"
                    required={requireRecipientEmail}
                    type="email"
                    value={form.email}
                    onChange={saveFieldValue("email")}
                    error={errors.email}
                  />
                  <AddRecipientField
                    id="add-recipient-company"
                    label="Recipient company"
                    value={form.toCompany}
                    onChange={saveFieldValue("toCompany")}
                  />
                </div>

                {canSetCountry && (
                  <div className="flex w-full flex-col gap-1.5">
                    {/* A picker (not free text) so a manual recipient can only carry a real ISO
                        code from the shared list — the same homologation bulk upload does, but here
                        it's guaranteed at entry. */}
                    <span className="font-lato text-[12px] font-semibold text-graphite uppercase tracking-[0.8px]">
                      Recipient country
                    </span>
                    <CountryDropdown
                      selectedCountry={form.recipientCountry}
                      onChange={(code) => setForm((prev) => ({ ...prev, recipientCountry: code }))}
                      fullWidth
                      menuZIndex={10001}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Step 2 — Sender fields */}
            {step === 2 && (
              <div className="flex flex-col gap-4">
                <div className="flex gap-4">
                  <AddRecipientField
                    id="add-recipient-sender-first"
                    label="Sender first name"
                    required
                    value={form.senderFirst}
                    onChange={saveFieldValue("senderFirst")}
                    error={errors.senderFirst}
                  />
                  <AddRecipientField
                    id="add-recipient-sender-last"
                    label="Sender last name"
                    value={form.senderLast}
                    onChange={saveFieldValue("senderLast")}
                    error={errors.senderLast}
                  />
                </div>

                <div className="flex gap-4">
                  <AddRecipientField
                    id="add-recipient-sender-email"
                    label="Sender email"
                    type="email"
                    value={form.fromEmail}
                    onChange={saveFieldValue("fromEmail")}
                    error={errors.fromEmail}
                  />
                  <AddRecipientField
                    id="add-recipient-sender-signature"
                    label="Sender signature"
                    value={form.senderSignature}
                    onChange={saveFieldValue("senderSignature")}
                  />
                </div>

                <AddRecipientField
                  id="add-recipient-sender-company"
                  label="Sender company"
                  required
                  value={form.senderCompany}
                  onChange={saveFieldValue("senderCompany")}
                  error={errors.senderCompany}
                  fullWidth
                />

                {showSendDate && (
                  <div className="flex gap-4">
                    <AddRecipientField
                      id="add-recipient-send-date"
                      label="Send on date"
                      type="date"
                      value={form.sendDate}
                      onChange={saveFieldValue("sendDate")}
                      error={errors.sendDate}
                      showLabel
                      min={TOMORROW}
                    />
                    <AddRecipientField
                      id="add-recipient-send-time"
                      label="Send at time"
                      type="time"
                      value={form.sendTime}
                      onChange={saveFieldValue("sendTime")}
                      error={errors.sendTime}
                      showLabel
                    />
                  </div>
                )}
              </div>
            )}

            {/* Footer */}
            <div className="flex justify-end gap-3 pt-1">
              {step === 1 ? (
                <>
                  <button
                    type="button"
                    onClick={onClose}
                    className="h-8.5 px-5 rounded-btn border border-dark-dust font-lato font-bold text-[11px] tracking-[1.98px] uppercase text-rodeo-black hover:bg-clear-dust transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    key="next"
                    type="button"
                    onClick={handleNext}
                    className="h-8.5 px-5 rounded-btn bg-rodeo-black font-lato font-bold text-[11px] tracking-[1.98px] uppercase text-white hover:bg-background-dark transition-colors"
                  >
                    Next
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleBack}
                    className="h-8.5 px-5 rounded-btn border border-dark-dust font-lato font-bold text-[11px] tracking-[1.98px] uppercase text-rodeo-black hover:bg-clear-dust transition-colors"
                  >
                    Back
                  </button>
                  {/* Own key: reusing Next's node would flip it to type=submit mid-click. */}
                  <button
                    key="submit"
                    type="submit"
                    className="h-8.5 px-5 rounded-btn bg-rodeo-black font-lato font-bold text-[11px] tracking-[1.98px] uppercase text-white hover:bg-background-dark transition-colors"
                  >
                    Add recipient
                  </button>
                </>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
